import { validateTimeframeExclusive } from './validators';
export class SortedTimeframeCollection {
    constructor(timeframes = []) {
        this._timeframes = timeframes;
        this.sortAscending();
    }
    get timeframes() {
        return this._timeframes;
    }
    insert(timeframe) {
        this.validate(timeframe);
        if (this.isEmpty()) {
            this.timeframes.push(timeframe);
            return;
        }
        if (this.findExistingTimeframe(timeframe) || this.checkRangeExistsInclusive(timeframe)) {
            return;
        }
        const newTimeframe = this.prepareTimeframe(timeframe);
        const timeframesToRemove = this.timeframes.filter(s => s.start >= newTimeframe.start && s.end <= newTimeframe.end);
        this.removeTimeframes(timeframesToRemove);
        this.timeframes.push(newTimeframe);
        this.sortAscending();
    }
    remove(timeframe) {
        this.validate(timeframe);
        const { start, end } = timeframe;
        const noTimeframeStartingEarlier = this.isNoTimeframeStartingEarlierThan(start);
        const noTimeframeEndingLater = this.isNoTimeframeEndingLaterThan(end);
        if (this.isEmpty()) {
            return;
        }
        if (this.findExistingTimeframe(timeframe)) {
            this.removeTimeframes([{ start, end }]);
            return;
        }
        if (noTimeframeStartingEarlier && noTimeframeEndingLater) {
            this.clear();
            return;
        }
        // If there are timeframes which contain start or end, they need to be cut accordingly
        this.cutOverlappingTimeframes(timeframe);
        this.filterTimeframesThatContain(timeframe);
        this.sortAscending();
    }
    clear() {
        this._timeframes = [];
    }
    validate(timeframe) {
        validateTimeframeExclusive(timeframe);
    }
    hasThisSecondOfTheDay(secondOfTheDay) {
        if (secondOfTheDay < 0 || secondOfTheDay > 86400) {
            throw new RangeError(`secondOfTheDay (${secondOfTheDay}) is outside the acceptable range 0 to 86400`);
        }
        return this._timeframes.some(s => secondOfTheDay >= s.start && secondOfTheDay <= s.end);
    }
    findExistingTimeframe(timeframe) {
        return this.timeframes.find(s => s.start === timeframe.start && s.end === timeframe.end);
    }
    checkRangeExistsInclusive(timeframe) {
        return this.timeframes.some(s => s.start <= timeframe.start && s.end >= timeframe.end);
    }
    isEmpty() {
        return this.timeframes.length === 0;
    }
    prepareTimeframe(timeframe) {
        return { start: this.getStartLimit(timeframe.start), end: this.getEndLimit(timeframe.end) };
    }
    isNoTimeframeStartingEarlierThan(startSecond) {
        return this.timeframes.every(s => startSecond <= s.start);
    }
    isNoTimeframeEndingLaterThan(endSecond) {
        return this.timeframes.every(s => endSecond >= s.end);
    }
    removeTimeframes(timeframesToRemove) {
        this._timeframes = this.timeframes.filter(s => !timeframesToRemove.some(toRemove => s.start === toRemove.start && s.end === toRemove.end));
    }
    findTimeframeContainingSecond(second, inclusive) {
        if (inclusive) {
            return this.timeframes.find(s => s.start <= second && s.end >= second);
        }
        return this.timeframes.find(s => s.start < second && s.end > second);
    }
    getStartLimit(startSecond) {
        var _a;
        // If we've found a range which includes start, we should use its start second
        const startTimeframe = this.findTimeframeContainingSecond(startSecond, true);
        // no range was found so use the startSecond
        return (_a = startTimeframe === null || startTimeframe === void 0 ? void 0 : startTimeframe.start) !== null && _a !== void 0 ? _a : startSecond;
    }
    getEndLimit(endSecond) {
        var _a;
        // If there's an existing timeframe which includes the endSecond, use it's `end` instead
        const endTimeframe = this.findTimeframeContainingSecond(endSecond, true);
        return (_a = endTimeframe === null || endTimeframe === void 0 ? void 0 : endTimeframe.end) !== null && _a !== void 0 ? _a : endSecond;
    }
    sortAscending() {
        this._timeframes = this.timeframes.sort((a, b) => {
            if (a.start === b.start) {
                return 0;
            }
            return a.start > b.start ? 1 : -1;
        });
    }
    /**
     * Filters timeframes that contain the start and end limits of the specified timeframe
     */
    filterTimeframesThatContain(timeframe) {
        const { start: startLimit, end: endLimit } = timeframe;
        this._timeframes = this.timeframes.filter(s => (s.start <= startLimit && s.end <= startLimit) || (s.start >= startLimit && s.end >= endLimit));
    }
    /**
     * If there are timeframes which intersect with the specified start or end seconds to be sealed, they need to be
     * cut to honor the sealed limits
     */
    cutOverlappingTimeframes(timeframe) {
        const timeFramesToBeDeleted = [];
        const { start, end } = timeframe;
        const startOverlappingRange = this.findTimeframeContainingSecond(start, false);
        const endOverlappingRange = this.findTimeframeContainingSecond(end, false);
        if (startOverlappingRange) {
            timeFramesToBeDeleted.push(startOverlappingRange);
            this.timeframes.push({ start: startOverlappingRange.start, end: start });
        }
        if (endOverlappingRange) {
            timeFramesToBeDeleted.push(endOverlappingRange);
            this.timeframes.push({ start: end, end: endOverlappingRange.end });
        }
        this.removeTimeframes(timeFramesToBeDeleted);
    }
}
